import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';
import { Popup, Toast } from 'vant';
import { env, Native } from '@hb/mbridge';
import router from '@/router/index';
import { UBT } from '@hb/tianqi/lib/ubt.h5';
import { getStylesBinder } from '~mall/common/utils/styles';
import { SkuDetailData } from '~mall/model/store/modules/ticket';
import {
  requestSkuDetail,
  fetchPayAfterUseSignData,
  checkIfPayAfterUseSuccess,
  reqBuyMultiGoods,
} from '~mall/model/apis/ticketApis';
import { getH5PayInst } from '~mall/model/apis/pay';
import { uiMgr } from '~mall/common/uiMgr';
import { equityAdapter } from '~mall/model/utils';
import { appQueryConfig } from '~mall/common/utils/url';
import { CusNetError } from '~mall/common/network/httpMgr';
import { versionCompare } from '~mall/common/utils/rentUtils';
import styles from './index.module.less';
import { isHelloFamilyApp } from '../../common/env/env';

const cx = getStylesBinder(styles);

export default defineComponent({
  name: 'PayAfterUse',
  setup: () => {
    /** 当前是否购买中——防重复点击 */
    const buying = ref(false);
    /** 详情数据 */
    const data = ref<SkuDetailData | null>(null);
    /** 是否展示成功弹窗 */
    const showSuccess = ref(false);
    /** 当前订单号 */
    const curOrderId = ref('');
    /** 权益列表 */
    const coupons = computed(() => {
      if (!data.value) {
        return [];
      }

      const { goodsEquitys } = data.value;
      return goodsEquitys
        .reduce<{
          desc: string | undefined;
          couponLeft: {
            num: string;
            word: string;
          };
        }[]>((acc, equity) => {
          const { virtualNum } = equity;
          const {
            descMap,
            couponLeft,
          } = equityAdapter(equity);
          const coupon = {
            couponLeft,
            desc: descMap.threshold,
          };
          return [...acc, ...Array.from(new Array(virtualNum)).map(() => coupon)];
        }, []);
    });
    /** 链接参数——商品唯一ID */
    const spuIdParam = computed(() => router.app.$route.query.spuId);
    /** 埋点公共参数 */
    const sharedUbtBizInfo = computed(() => ({
      adsource: appQueryConfig.adsource,
      spuId: spuIdParam.value,
    }));
    /** toast提示曝光埋点 */
    const ubtToastMe = (msg?: string | number) => {
      UBT.moduleExpose({
        pageId: 'platform_couponBNPL',
        categoryId: 'platform',
        moduleId: 'platform_Pageprompts',
        contentId: 'platform_Pageprompts',
        exposureTimes: 1,
        businessInfo: {
          Pageprompts: msg,
          ...sharedUbtBizInfo.value,
        },
      });
    };
    /** toast提示并发送埋点 */
    const ubtToast = (...args: Parameters<typeof Toast>) => {
      const [optOrMsg] = args;
      const msg = typeof optOrMsg === 'object'
        ? optOrMsg.message
        : optOrMsg;
      ubtToastMe(msg);
      Toast(...args);
    };
    /** 加载商品详情数据 */
    const loadData = async () => {
      if (!spuIdParam.value) {
        return;
      }

      const toast = Toast.loading({
        message: '加载中...',
        forbidClick: true,
      });

      try {
        data.value = await requestSkuDetail({ spuId: spuIdParam.value });
      } catch {
        // noop
      } finally {
        toast.clear();
      }
    };
    loadData();

    /** 轮询信用冻结是否成功 */
    const loopQueryOrderFrozen = async () => {
      const toast = Toast.loading({
        message: '下单中...',
        forbidClick: true,
      });
      const result = await new Promise<boolean>((resolve) => {
        const start = Date.now();
        const loopSearch = async () => {
          if (await checkIfPayAfterUseSuccess(curOrderId.value)) {
            resolve(true);
          } else if (Date.now() - start > 2000) {
            resolve(false);
          } else {
            window.setTimeout(loopSearch, 500);
          }
        };
        loopSearch();
      });
      toast.clear();
      return result;
    };

    /**
     * 下单
     * @param isSigned 用户是否已签约
     * @returns
     */
    const reqOrder = async (isSigned: boolean) => {
      const {
        salePrice,
        skuId,
        spuId,
        promotionInfos,
      } = data.value!;
      return reqBuyMultiGoods({
        isCreditAuthOrder: true,
        isSigned,
        payAmount: salePrice,
        itemList: [{
          skuId,
          spuId,
          salePrice,
          num: 1,
          promotionInfos,
        }],
      });
    };
    /** 签约下单 */
    const reqSignOrder = async () => {
      const {
        payOrderId,
        payMoney,
        orderId,
      } = await reqOrder(false);
      await getH5PayInst().enjoyAfterPay({
        freezeOrderVoucherNo: payOrderId,
        freezeAmount: payMoney,
        times: 10,
      });
      return orderId;
    };
    /** 前往订单详情页 */
    const goDetail = () => {
      if (!curOrderId.value) {
        return;
      }

      uiMgr.pushPage(`/order/${curOrderId.value}`);
    };
    /** 处理购买按钮的点击 */
    const handleBuy = async () => {
      UBT.clickButton({
        pageId: 'platform_couponBNPL',
        buttonName: 'platform_gopay',
        categoryId: 'platform',
        businessInfo: sharedUbtBizInfo.value,
      });

      if (buying.value) {
        return;
      }

      // * 判断端口
      if (!env.isEasyBikeApp) {
        ubtToast('请前往哈啰APP参加活动');
        return;
      }

      if (versionCompare(env.appVersion, '6.64.0') < 0) {
        ubtToast('当前APP版本不支持先享后付，请下载最新版本应用');
        return;
      }

      buying.value = true;

      try {
        // * 查询签约状态
        const {
          code: signCode,
          data: signData,
        } = await fetchPayAfterUseSignData();

        if (signCode !== 0) {
          ubtToast('签约失败');
          return;
        }

        const signed = signData?.status === 0;

        if (!signed) {
          // * 签约下单
          curOrderId.value = await reqSignOrder();
        } else {
          curOrderId.value = await new Promise<string>((resolve, reject) => {
            ubtToast({
              message: '您已开通先享后付功能',
              duration: 1000,
              onClose: async () => {
                try {
                  const { orderId } = await reqOrder(true);
                  resolve(orderId);
                } catch (e) {
                  reject(e);
                }
              },
            });
          });
        }

        const success = await loopQueryOrderFrozen();

        if (success) {
          showSuccess.value = true;
        } else {
          ubtToast({
            message: '下单成功',
            duration: 1000,
            onClose: goDetail,
          });
        }
      } catch (e) {
        if (e instanceof Error) {
          ubtToast(e.message);
        } else if (typeof e === 'object') {
          const { netErrMsg } = e as CusNetError;

          if (netErrMsg) {
            ubtToastMe(netErrMsg);
          }
        }
      } finally {
        buying.value = false;
      }
    };
    /** 点击查看详情 */
    const handleShowDetail = () => {
      UBT.clickButton({
        pageId: 'platform_couponBNPL',
        buttonName: 'platform_payresultclick',
        categoryId: 'platform',
        businessInfo: sharedUbtBizInfo.value,
      });

      showSuccess.value = false;
      goDetail();
    };
    /** 点击我知道了 */
    const handleGot = () => {
      UBT.clickButton({
        pageId: 'platform_couponBNPL',
        buttonName: 'platform_payresultclick2',
        categoryId: 'platform',
        businessInfo: sharedUbtBizInfo.value,
      });

      showSuccess.value = false;
      // * 刷新数据
      loadData();
    };
    onMounted(() => {
      UBT.pageView({
        pageId: 'platform_couponBNPL',
        categoryId: 'platform',
        businessInfo: sharedUbtBizInfo.value,
      });

      if (isHelloFamilyApp) {
        Native.callNative({
          classMap: 'NavBar',
          method: 'setStatusBar',
          params: { isShowStatusBar: true },
        });
        Native.callNative({
          classMap: 'NavBar',
          method: 'setStatusBarColor',
          params: { isShowStatusBar: true, isDarkFont: false, colorCode: '#ffffff' },
        });
      }
    });
    onBeforeUnmount(() => {
      UBT.pageViewOut({
        pageId: 'platform_couponBNPL',
        categoryId: 'platform',
        businessInfo: sharedUbtBizInfo.value,
      });
    });
    return () => {
      const {
        costPrice,
        salePrice,
        useNotice,
      } = data.value || {};
      return (
        <div class={cx('page')}>
          <div class={cx('main')}>
            <div class={cx('header')}>
              <picture>
                <source
                  srcset="https://m.hellobike.com/resource/helloyun/18121/pT-y0RnDTY.webp 2x, https://m.hellobike.com/resource/helloyun/18121/xSG9hAClz8.webp 3x"
                  type="image/webp"
                />
                <img
                  class={cx('bg')}
                  src="https://m.hellobike.com/resource/helloyun/18121/SzGcUYni5Y.png"
                  srcset="https://m.hellobike.com/resource/helloyun/18121/SzGcUYni5Y.png 2x, https://m.hellobike.com/resource/helloyun/18121/2eCZXQfL7B.png 3x"
                  width="375"
                  height="435"
                />
              </picture>
              {costPrice && <div class={cx('title')}>价值{costPrice}元权益包</div>}
              {salePrice && <div class={cx('pay-limit')}>封顶支付{salePrice}元</div>}
            </div>
            {costPrice && <div class={cx('sub-title')}>开通即得价值{costPrice}元权益包</div>}
            <div class={cx('coupon-list')}>{coupons.value.map(({
              desc,
              couponLeft,
            }) => (
              <div class={cx('coupon')}>
                <div class={cx('coupon__head')}>
                  <span class={cx('coupon__amount')}>{couponLeft.num}</span>
                  <span class={cx('coupon__unit')}>{couponLeft.word}</span>
                </div>
                <div class={cx('coupon__tail')}>{desc}</div>
              </div>
            ))}</div>
            <div class={cx('rule')}>
              {useNotice && <div class={cx('rule__tip')} domPropsInnerHTML={useNotice}></div>}
              <div class="rule__act"></div>
            </div>
          </div>
          <div class={cx('footer')}>
            <div
              class={cx('buy')}
              v-node-expose={{
                exposeData: {
                  pageId: 'platform_couponBNPL',
                  categoryId: 'platform',
                  moduleId: 'platform_gopay',
                  contentId: 'platform_gopay',
                  exposureTimes: 1,
                  businessInfo: sharedUbtBizInfo.value,
                },
              }}
              onClick={() => {
                if (!data.value) {
                  return;
                }

                handleBuy();
              }}
            ></div>
          </div>
          <div class="aside">
            <Popup
              value={showSuccess.value}
              onInput={(val: boolean) => { showSuccess.value = val; }}
              position="bottom"
              class={cx('succ-popup')}
              close-on-click-overlay={false}
            >
              <div
                class={cx('succ')}
                v-node-expose={{
                  exposeData: {
                    pageId: 'platform_couponBNPL',
                    categoryId: 'platform',
                    moduleId: 'platform_payresult',
                    contentId: 'platform_payresult',
                    exposureTimes: 1,
                    businessInfo: sharedUbtBizInfo.value,
                  },
                }}
              >
                <div class={cx('succ__header')}>先享成功</div>
                <div class={cx('succ__content')}>
                  <img
                    src="https://m.hellobike.com/resource/helloyun/18121/mv-r25csWr.png"
                    alt="success"
                    class={cx('succ__icon')}
                    width="39"
                    height="39"
                  />
                  <div class={cx('succ__desc')}>权益券包将会在几分钟内自动发放到您的账户中<br />稍后可前往我的优惠券列表页中查看详情及使用优惠券</div>
                  <div class={cx('succ__action-list')}>
                    <div class={cx('succ__action', 'succ__action_detail')} onClick={handleShowDetail}>查看详情</div>
                    <div class={cx('succ__action', 'succ__action_got')} onClick={handleGot}>我知道了</div>
                  </div>
                </div>
              </div>
            </Popup>
          </div>
        </div>
      );
    };
  },
});
